import { useCallback, useContext, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import {
  ActiveSuggestedAction,
  PausedSuggestedAction,
  SUGGESTED_ACTION_MUTE_DAYS,
  SuggestedActionsResponse,
} from 'router/subrouters/DataRoom/models/Dashboard';
import { addDaysToDate } from 'utils';
import { SUGGESTED_ACTIONS_QUERY_KEY } from '../../../hooks/useSuggestedActionsQuery';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import useCallbackAsync from 'hooks/useCallbackAsync';
import { SuggestedActionUndoHandlers } from './useGetUndoActions';

const useGetThreeDotsOptions = (
  suggestion: ActiveSuggestedAction,
  {
    setSuggestion,
    setDismissUndoTimeout,
    setDontRemindUndoTimeout,
  }: SuggestedActionUndoHandlers,
) => {
  const {
    dataRoom: { _id },
  } = useContext(DataRoomContext);

  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const { type } = suggestion;

  const moveSuggestionToPaused = useCallback(
    (pausedSuggestion: PausedSuggestedAction) => {
      queryClient.setQueriesData<SuggestedActionsResponse>(
        { queryKey: [SUGGESTED_ACTIONS_QUERY_KEY] },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            active: oldData.active.filter((item) => item.type !== type),
            paused: [...oldData.paused, pausedSuggestion],
          };
        },
      );
    },
    [queryClient, type],
  );

  const [onDismiss, isDissmising] = useCallbackAsync(async () => {
    await dataRoomApi.room.muteSuggestedAction(
      _id,
      type,
      SUGGESTED_ACTION_MUTE_DAYS,
    );

    moveSuggestionToPaused({
      ...suggestion,
      mutedAt: new Date(),
      mutedUntil: addDaysToDate(SUGGESTED_ACTION_MUTE_DAYS),
    });
    setSuggestion(suggestion);
    setDismissUndoTimeout();
  });

  const [onDontRemindMe, isDontReminding] = useCallbackAsync(async () => {
    await dataRoomApi.room.muteSuggestedAction(_id, type);

    moveSuggestionToPaused({
      ...suggestion,
      mutedAt: new Date(),
      mutedUntil: null,
    });
    setSuggestion(suggestion);
    setDontRemindUndoTimeout();
  });

  const threeDotsOptions = useMemo(
    () => [
      {
        label: t('dismiss'),
        disabled: isDissmising,
        onClick: onDismiss,
      },
      {
        label: t('dontRemindMeAgain'),
        disabled: isDontReminding,
        onClick: onDontRemindMe,
      },
    ],
    [isDissmising, isDontReminding, onDismiss, onDontRemindMe, t],
  );

  return {
    isDissmising,
    isDontReminding,
    threeDotsOptions,
  };
};

export default useGetThreeDotsOptions;
